import {
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  TEMP_TOKEN_KEY,
  TOKEN_EXPIRES_AT_KEY,
  USER_CONTEXT_KEY,
} from '@/constants/auth';
import type { SessionPayload, TokenPayload, UserContext } from '@/types/auth';

const isBrowser = typeof window !== 'undefined';

const readStorage = (key: string) => {
  if (!isBrowser) {
    return null;
  }

  return window.localStorage.getItem(key);
};

const writeStorage = (key: string, value?: string | number) => {
  if (!isBrowser) {
    return;
  }

  if (value === undefined || value === null || value === '') {
    window.localStorage.removeItem(key);
    return;
  }

  window.localStorage.setItem(key, String(value));
};

export const getStoredAccessToken = () => readStorage(ACCESS_TOKEN_KEY) ?? '';

export const getStoredRefreshToken = () => readStorage(REFRESH_TOKEN_KEY) ?? '';

export const getStoredExpiresAt = () => {
  const value = readStorage(TOKEN_EXPIRES_AT_KEY);
  return value ? Number(value) : undefined;
};

export const getStoredTempToken = () => readStorage(TEMP_TOKEN_KEY) ?? '';

export const getStoredUserContext = (): UserContext | undefined => {
  const rawValue = readStorage(USER_CONTEXT_KEY);
  if (!rawValue) {
    return undefined;
  }

  try {
    return JSON.parse(rawValue) as UserContext;
  } catch (_error) {
    return undefined;
  }
};

// 所有登录态都统一存 localStorage，保证刷新页面后还能恢复。
export const setStoredTokens = (payload: TokenPayload) => {
  writeStorage(ACCESS_TOKEN_KEY, payload.accessToken);
  writeStorage(REFRESH_TOKEN_KEY, payload.refreshToken);
  writeStorage(TOKEN_EXPIRES_AT_KEY, payload.expiresAt);
};

export const setStoredTempToken = (tempToken?: string) => {
  writeStorage(TEMP_TOKEN_KEY, tempToken);
};

export const setStoredUserContext = (context?: UserContext) => {
  if (!context) {
    writeStorage(USER_CONTEXT_KEY);
    return;
  }

  writeStorage(USER_CONTEXT_KEY, JSON.stringify(context));
};

export const clearStoredTokens = () => {
  writeStorage(ACCESS_TOKEN_KEY);
  writeStorage(REFRESH_TOKEN_KEY);
  writeStorage(TOKEN_EXPIRES_AT_KEY);
  writeStorage(TEMP_TOKEN_KEY);
  writeStorage(USER_CONTEXT_KEY);
};

export const getStoredSession = (): SessionPayload | undefined => {
  // 登录后如果需要先选组织/店铺，可能只有 tempToken，没有 accessToken。
  const accessToken = getStoredAccessToken();
  const tempToken = getStoredTempToken();
  const userContext = getStoredUserContext();

  if (!accessToken && !tempToken) {
    return undefined;
  }

  return {
    accessToken,
    refreshToken: getStoredRefreshToken() || undefined,
    expiresAt: getStoredExpiresAt(),
    tempToken: tempToken || undefined,
    userContext,
  };
};
